<!-- eslint-disable vue/require-explicit-emits -->
<template>
  <component
    :is="direction === 'vertical' ? 'iColumn' : 'iRow'"
    :class="{
      ...classList,
      'empty-layout': isEmpty && !hasABorder,
      'root-layout': isRootLayout,
      'layout': true,
    }"
    v-bind="{
      ...myLayoutProps,
      'data-identifier': identifier,
      'data-parent-id': identifier,
      'draggable': editMode && isDraggable(identifier, configuration) && !breakpoint
    }"
    :align="direction === 'vertical' ? align : verticalAlign"
    :vertical-align="direction === 'vertical' ? verticalAlign : align"
    v-on="myEventListeners"
  >
    <Teleport
      v-if="(isDynamicAppComponent ? true : canEditPublication)
        && showContextualMenu
        && contextMenuFixedPositionStyle.top
        && contextMenuFixedPositionStyle.left"
      to="body"
    >
      <DynamicComponentContextualMenu
        ref="contextualMenu"
        v-bind="contextMenuAttrs"
        v-on="contextMenuEvents"
      />
    </Teleport>
    <div
      v-if="canResizeHorizontally && (mouseOver || selectedHoverBorder)"
      class="resize-handle left"
      @mousedown.stop="handleMouseDownResize($event, 'left')"
    />
    <div
      v-if="canResizeHorizontally && (mouseOver || selectedHoverBorder)"
      class="resize-handle right"
      @mousedown.stop="handleMouseDownResize($event, 'right')"
    />
    <div
      v-if="canResizeVertically && (mouseOver || selectedHoverBorder)"
      class="resize-handle top"
      @mousedown.stop="handleMouseDownResize($event, 'top')"
    />
    <div
      v-if="canResizeVertically && (mouseOver || selectedHoverBorder)"
      class="resize-handle bottom"
      @mousedown.stop="handleMouseDownResize($event, 'bottom')"
    />
    <!-- Canvas-only note; no data-edit-identifier, so drop slots skip it. -->
    <iText
      v-if="editMode && canvasHint"
      variant="subtle"
      font-size="small"
      data-test="container-canvas-hint"
    >
      {{ canvasHint }}
    </iText>
    <slot />
  </component>
</template>

<script>
import {
  LayoutElementMixin,
} from "%/dynamic-component-editor/standard-elements/layouts/layout-element-mixin";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import LayoutWrap from "%/dynamic-component-editor/options/layout-wrap";
import RootLayoutPositionOptions from "%/dynamic-component-editor/bundles/root-layout-position-options";
import DynamicComponentContextualMenu
  from "%/dynamic-component-editor/components/DynamicComponentContextualMenu.vue";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import PositioningOptions from "%/dynamic-component-editor/bundles/positioning-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BackgroundPosition from "%/dynamic-component-editor/options/background-position";
import BackgroundSize from "%/dynamic-component-editor/options/background-size";
import BackgroundRepeat from "%/dynamic-component-editor/options/background-repeat";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import AnimationEffectsOptions from "%/dynamic-component-editor/bundles/animation-effects-options";
import LayoutPositionOptions from "%/dynamic-component-editor/bundles/layout-position-options";
import LayoutAlign from "%/dynamic-component-editor/options/layout-align";
import LayoutAlignVertical from "%/dynamic-component-editor/options/layout-align-vertical";
import StaticLayoutDirection from "%/dynamic-component-editor/options/static-layout-direction";

export default {
  name: "Container",
  components: { DynamicComponentContextualMenu },
  mixins: [LayoutElementMixin],
  elementPanelSettings: {
    tags: ["container", "layout", "group", "box", "wrapper"],
    displayName: "Container",
  },
  data() {
    return {
      LayoutWrap,
      BaseOption,
      BackgroundRepeat,
      BackgroundPosition,
      BackgroundColor,
      BackgroundSize,
      LayoutAlign,
      LayoutAlignVertical,
      StaticLayoutDirection,
    };
  },
  deprecated: true,
  optionBundles: [
    PositioningOptions,
    LayoutPositionOptions,
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    AnimationEffectsOptions,
    new EffectsOptions({}, {
      "text-shadow": true,
    }),
    SpacingOptions,
    BackgroundOptions,
  ],
  computed: {
    // A subclass names what the container is for members; plain containers show nothing.
    canvasHint() {
      return "";
    },
    direction() {
      return this.getOptionValue(StaticLayoutDirection);
    },
    myAlign() {
      return this.direction === "vertical" ? this.align : this.verticalAlign;
    },
    align() {
      const value = this.getOptionValue(LayoutAlign);
      if (this.getOptionValue(StaticLayoutDirection) === "vertical") {
        switch (value) {
          case "start":
            return "left";
          case "end":
            return "right";
          default:
            return "center";
        }
      } else {
        switch (value) {
          case "start":
            return "top";
          case "end":
            return "bottom";
          default:
            return "middle";
        }
      }
    },
    verticalAlign() {
      const value = this.getOptionValue(LayoutAlignVertical);
      if (this.getOptionValue(StaticLayoutDirection) === "vertical") {
        switch (value) {
          case "start":
            return "top";
          case "end":
            return "bottom";
          case "center":
            return "middle";
          case "between":
            return "between";
          case "around":
            return "around";
          case "evenly":
            return "evenly";
          default:
            return "top";
        }
      } else {
        switch (value) {
          case "start":
            return "left";
          case "end":
            return "right";
          case "center":
            return "center";
          case "between":
            return "between";
          case "around":
            return "around";
          case "evenly":
            return "evenly";
          default:
            return "left";
        }
      }
    },
  },
  mounted() {
    if (this.editMode) {
      this.configureOptionsAndBuilder();
    }
  },
  methods: {
    configureOptionsAndBuilder() {
      if (this.isRootLayout) {
        this.setEditorElementConfig(this.configuration.identifier, "isDeletable", false);
        this.setEditorElementConfig(this.configuration.identifier, "isDraggable", false);
        this.setEditorElementConfig(this.configuration.identifier, "isDuplicatable", false);
        this.setEditorElementConfig(this.configuration.identifier, "canWrap", false);
        this.setEditorElementConfig(this.configuration.identifier, "canCreateCustomComponent", false);

        if (this.isPage) {
          this.setEditorElementOptionsConfig(this.configuration.identifier, {
            bundles: [
              new PositioningOptions({
                "default.positioning": {
                  position: "relative",
                  top: "unset",
                  right: "unset",
                  bottom: "unset",
                  left: "unset",
                },
              }),
              RootLayoutPositionOptions,
              new AppearanceOptions({}, {
                "visible": {
                  disable: true,
                  message: "Visibility can not be toggled for root containers",
                },
                "border-radius": false,
              }),
              SpacingOptions,
              BackgroundOptions,
            ],
          });
        }
      }
    },
  },
};
</script>
